import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import LoadingComponent from '../loadingComponent';
import withRouter from '../lib/withRouter';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import './ActionQueue.scss';

class ActionQueue extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.toast = React.createRef();

        this.state = {
            loading: false,
            actions: [],
            totalRecords: 0,
            lazyParams: {
                first: 0,
                rows: 10,
                page: 1,
            },
            filters: {
                status: this.props.isApprovalMode ? 'NEEDS_APPROVAL' : null,
                assignedTo: null,
                search: ''
            },
            // UI state for inputs before searching
            tempFilters: {
                status: this.props.isApprovalMode ? 'NEEDS_APPROVAL' : null,
                assignedTo: null,
                search: ''
            },
            
            // Dialog States
            overrideDialog: false,
            outcomeDialog: false,
            selectedAction: null,
            
            // Form Data
            overrideData: {
                actionType: '',
                assignedTo: '',
                scheduledDate: null,
                justification: ''
            },
            outcomeData: {
                status: '',
                remarks: '',
                promiseDate: null
            },
            
            // History State
            historyDialog: false,
            studentHistory: [],
            selectedStudent: null,

            // Manual Action State
            manualDialog: false,
            manualData: {
                studentId: '',
                enrollmentNo: '',
                actionType: 'CALL',
                assignedTo: 'ACCOUNTS',
                scheduledDate: new Date(),
                remarks: ''
            },
            
            // Bulk Selection
            selectedActions: [],
            
            // Debounce for follow-up button
            followUpDebounce: {}
        };

        // Call outcome options (Synced with CallTasks.js)
        this.outcomeOptions = [
            { label: 'Connected - Payment Promised', value: 'PROMISED' },
            { label: 'Connected - Already Paid', value: 'PAID' },
            { label: 'Connected - Need Time', value: 'CONNECTED' },
            { label: 'No Answer', value: 'NO_ANSWER' },
            { label: 'Busy', value: 'BUSY' },
            { label: 'Wrong Number', value: 'WRONG_NUMBER' },
            { label: 'Refused to Talk', value: 'REFUSED' },
            { label: 'Callback Requested', value: 'CALLBACK_REQUESTED' }
        ];
    }

    componentDidMount() {
        this.fetchActions();
    }

    componentDidUpdate(prevProps) {
        // Reload if switching between Queue and Approval modes via Router
        if (this.props.isApprovalMode !== prevProps.isApprovalMode) {
             const defaultStatus = this.props.isApprovalMode ? 'NEEDS_APPROVAL' : null;
             this.setState({
                filters: {
                    status: defaultStatus,
                    assignedTo: null,
                    search: ''
                },
                tempFilters: {
                    status: defaultStatus,
                    assignedTo: null,
                    search: ''
                },
                selectedActions: [] // Clear selection on mode switch
            }, this.fetchActions);
        }
    }

    fetchActions = async () => {
        const { isApprovalMode } = this.props;
        const academicYear = localStorage.getItem('userAcademicYear');

        this.setState({ loading: true });

        try {
            const { lazyParams, filters } = this.state;
            let url = `${baseUrlAdmin}/fee-decision/actions?academicYear=${academicYear}&page=${lazyParams.page}&limit=${lazyParams.rows}`;
            
            // Enforce Status Filter for Approval Mode
            if (isApprovalMode) {
                url += `&status=NEEDS_APPROVAL`;
            } else if (filters.status) {
                url += `&status=${filters.status}`;
            }

            if (filters.assignedTo) url += `&assignedTo=${filters.assignedTo}`;
            if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;

            const response = await this.service.get(url, true);

            if (response?.status && response?.res?.success) {
                this.setState({
                    actions: response.res.data,
                    totalRecords: response.res.meta.total
                });
            }
        } catch (error) {
            console.error(error);
            this.showToast('error', 'Error', 'Failed to fetch actions');
        } finally {
            this.setState({ loading: false });
        }
    }

    generateActions = async () => {
        const { user } = this.props;
        const academicYear = localStorage.getItem('userAcademicYear');
        this.setState({ loading: true });
        try {
            const url = `${baseUrlAdmin}/fee-decision/actions/generate?academicYear=${academicYear}`;
            await this.service.get(url, true);
            this.showToast('success', 'Success', 'Actions generated based on latest Risk Scores');
            this.fetchActions();
        } catch (error) {
            this.showToast('error', 'Error', error.message);
            this.setState({ loading: false });
        }
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({ severity, summary, detail, life: 3000 });
    }

    onPage = (event) => {
        this.setState({
            lazyParams: { ...this.state.lazyParams, first: event.first, rows: event.rows, page: event.page + 1 }
        }, this.fetchActions);
    }

    // --- Action Handlers ---

    getCurrentUserId = () => {
        const { user } = this.props;
        return user?._id || user?.userId || user?.user_id || user?.id || null;
    }

    getUserOpenActionIdForStudent = (studentId) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const { actions } = this.state;
        const openAction = actions.find((a) => {
            if (!a || a.studentId !== studentId) return false;
            if (!['PENDING', 'NEEDS_APPROVAL'].includes(a.status)) return false;
            const scheduled = new Date(a.scheduledDate);
            return scheduled >= today && scheduled < tomorrow;
        });

        return openAction?._id || null;
    }
    
    hasUserOpenActionForStudent = (studentId) => {
        const userId = this.getCurrentUserId();
        if (!userId) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const { actions } = this.state;
        const openAction = actions.find((a) => {
            if (!a || a.studentId !== studentId) return false;
            const createdBy = a.createdBy?._id || a.createdBy;
            if (createdBy && createdBy.toString() !== userId.toString()) return false;
            if (!['PENDING', 'NEEDS_APPROVAL'].includes(a.status)) return false;
            const scheduled = new Date(a.scheduledDate);
            return scheduled >= today && scheduled < tomorrow;
        });

        return Boolean(openAction);
    }

    approveAction = (action) => {
        confirmDialog({
            message: `Approve ${action.actionType} for ${action.studentName}?`,
            header: 'Confirmation',
            icon: 'pi pi-check-circle',
            accept: async () => {
                await this.updateAction(action._id, { status: 'APPROVED' });
            }
        });
    }

    fetchStudentHistory = async (studentId) => {
        try {
            const url = `${baseUrlAdmin}/fee-decision/actions/history/${studentId}`;
            const res = await this.service.get(url, true);
            if (res?.status) {
                this.setState({ studentHistory: res.res.data || [] });
            }
        } catch (error) {
            console.error(error);
        }
    }

    viewHistory = (action) => {
        this.setState({ 
            historyDialog: true, 
            selectedStudent: { name: action.studentName, id: action.studentId } 
        });
        this.fetchStudentHistory(action.studentId);
    }

    openOverride = (action) => {
        this.setState({
            selectedAction: action,
            overrideDialog: true,
            overrideData: {
                actionType: action.actionType,
                assignedTo: action.assignedTo,
                scheduledDate: new Date(action.scheduledDate),
                justification: action.overrideJustification || ''
            }
        });
    }

    submitOverride = async () => {
        const { selectedAction, overrideData } = this.state;
        if (!overrideData.justification) {
            this.showToast('warn', 'Validation', 'Justification is required for override');
            return;
        }

        await this.updateAction(selectedAction._id, {
            isOverride: true,
            ...overrideData
        });
        this.setState({ overrideDialog: false });
    }

    openOutcome = (action) => {
        // Pre-populate if editing existing outcome
        const existingOutcome = action.outcome || {};
        
        // Handle migration of legacy outcome strings to new codes
        let status = existingOutcome.status || '';
        if (status === 'Promised to Pay') status = 'PROMISED';
        if (status === 'No Answer') status = 'NO_ANSWER';
        if (status === 'Number Busy') status = 'BUSY';
        if (status === 'Refused to Pay') status = 'REFUSED';
        if (status === 'Paid Online') status = 'PAID';

        this.setState({
            selectedAction: action,
            outcomeDialog: true,
            outcomeData: { 
                status: status,
                remarks: existingOutcome.remarks || '',
                promiseDate: existingOutcome.promiseDate ? new Date(existingOutcome.promiseDate) : null
            }
        });
    }

    submitOutcome = async () => {
        const { selectedAction, outcomeData } = this.state;
         if (!outcomeData.status) {
            this.showToast('warn', 'Validation', 'Please select an outcome status');
            return;
        }

        if (outcomeData.status === 'PROMISED' && !outcomeData.promiseDate) {
             this.showToast('warn', 'Validation', 'Please enter the Promised Date');
             return;
        }

        await this.updateAction(selectedAction._id, {
            outcome: outcomeData
        });
        this.setState({ outcomeDialog: false });
    }

    updateAction = async (id, data) => {
        this.setState({ loading: true });
        try {
            const url = `${baseUrlAdmin}/fee-decision/actions/${id}`;
            const res = await this.service.put(url, data, true);
            if (res?.status) {
                this.showToast('success', 'Success', 'Action updated');
                // Wait a moment for DB commit before refreshing
                await new Promise(resolve => setTimeout(resolve, 500));
                await this.fetchActions();
            } else {
                this.showToast('error', 'Error', 'Failed to update action');
                this.setState({ loading: false });
            }
        } catch (error) {
            this.showToast('error', 'Error', error.message);
            this.setState({ loading: false });
        }
    }

    // --- Render Helpers ---

    riskBody = (rowData) => {
        const band = rowData.riskBand?.toUpperCase();
        let severity = null;
        let style = null;

        if (rowData.riskImproved) {
             severity = 'success';
        } else {
             switch (band) {
                case 'CRITICAL': style = { backgroundColor: '#FFA6BC', color: '#BF0031' }; break;
                case 'HIGH': style = { backgroundColor: '#FFEDD5', color: '#C25400' }; break; // Explicit Dark Orange
                case 'MEDIUM': style = { backgroundColor: '#FEF9C3', color: '#854D0E' }; break; // Explicit Yellow
                case 'LOW': style = { backgroundColor: '#DCFCE7', color: '#268900' }; break;
                default: style = { backgroundColor: '#E6E8EA', color: '#4B5563' };
            }
        }
        
        let icon = null;
        if (rowData.riskMismatch) {
            icon = <i className="pi pi-exclamation-triangle ml-2 text-orange-600" title="Risk band updated since action was created"></i>;
        }
        if (rowData.riskImproved) {
            icon = <i className="pi pi-check-circle ml-2 text-green-600" title="Risk improved - escalation may be auto-cancelled"></i>;
        }
        
        return (
            <div className="flex align-items-center gap-2">
                <Tag value={rowData.riskBand} severity={severity} style={style} />
                <span className="text-sm text-gray-500">({rowData.riskScore})</span>
                {icon}
            </div>
        );
    }

    statusBody = (rowData) => {
        let statusDisplay = rowData.status.replace('_', ' ');
        let statusClass = rowData.status.toLowerCase();
        let icon = null;
        
        if (rowData.needsAttention) {
            icon = <i className="pi pi-flag ml-2 text-red-600" title={rowData.attentionReason || 'Needs attention'}></i>;
        }
        
        if (rowData.status === 'OVERRIDDEN' && rowData.overrideReason === 'Risk band improved - payment received') {
            statusDisplay = 'Auto-Cancelled';
            icon = <i className="pi pi-check-circle ml-2 text-green-600" title="Auto-cancelled: Risk improved after payment"></i>;
        }
        
        return (
            <span className={`status-badge ${statusClass}`}>
                {statusDisplay}
                {icon}
            </span>
        );
    }

    dateBody = (rowData) => {
        const date = new Date(rowData.scheduledDate);
        return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
    }

    // Helper to check for breach
    isPromiseBreached = (rowData) => {
        if (!rowData.promiseDate) return false;
        if (rowData.promiseStatus === 'PAID' || rowData.promiseStatus === 'FULFILLED') return false;
        
        const pDate = new Date(rowData.promiseDate);
        const today = new Date();
        today.setHours(0,0,0,0);
        
        // Use local date part of promise date for comparison
        // Note: backend date is usually UTC, so this correctly compares dates
        const promiseDate = new Date(pDate.getFullYear(), pDate.getMonth(), pDate.getDate());
        
        return promiseDate < today;
    }

    lastOutcomeBody = (rowData) => {
        // Use lastOutcome fields projected from the backend aggregation
        if (!rowData.lastOutcome || rowData.lastOutcome === 'None') return '-';
        
        let display = rowData.lastOutcome;

        // Try to map code to label
        if (this.outcomeOptions) {
            const option = this.outcomeOptions.find(o => o.value === rowData.lastOutcome);
            if (option) display = option.label;
            // Shorten common long labels for table display if needed
            if (display === 'Connected - Payment Promised') display = 'Promised';
            if (display === 'Connected - Already Paid') display = 'Paid';
            if (display === 'Connected - Need Time') display = 'Need Time';
        }
        
        // Append Promise Date if available
        if ((rowData.lastOutcome === 'Promised to Pay' || rowData.lastOutcome === 'PROMISED') && rowData.promiseDate) {
            const pDate = new Date(rowData.promiseDate);
            // Use UTC methods to avoid timezone shifts
            const formattedDate = `${pDate.getUTCDate().toString().padStart(2, '0')}/${(pDate.getUTCMonth() + 1).toString().padStart(2, '0')}/${pDate.getUTCFullYear()}`;
            display = `Promised (${formattedDate})`;

            // Check for Promise Breach
            if (this.isPromiseBreached(rowData)) {
                display = (
                    <span>
                        {display}<br/>
                        <Tag severity="danger" value="Promise Broken" className="mt-1" />
                    </span>
                );
            }
        }
        
        // Tooltip or small text for Remarks and Who
        return (
            <div className="flex flex-column gap-1">
                <span className="font-semibold">{display}</span>
                {rowData.lastOutcomeRemarks && (
                    <span className="text-xs text-gray-500" title={rowData.lastOutcomeRemarks}>
                        {rowData.lastOutcomeRemarks.length > 20 ? rowData.lastOutcomeRemarks.substring(0, 20) + '...' : rowData.lastOutcomeRemarks}
                    </span>
                )}
                {rowData.lastOutcomeBy && rowData.lastOutcomeBy !== 'Unknown' && (
                    <span className="text-xs surface-200 border-round px-1 max-w-min white-space-nowrap">
                        By: {rowData.lastOutcomeBy}
                    </span>
                )}
            </div>
        );
    }

    // Profession Context Body Template
    professionBody = (rowData) => {
        if (!rowData.professionContext || !rowData.professionContext.category) {
            return <span className="text-muted text-xs">Not Set</span>;
        }
        
        const pc = rowData.professionContext;
        const fallbackPatternByCategory = {
            SALARIED: 'MONTHLY_FIXED',
            RETIRED: 'MONTHLY_FIXED',
            FARMER: 'SEASONAL',
            BUSINESS: 'VARIABLE',
            PROFESSIONAL: 'VARIABLE',
            DAILY_WAGE: 'DAILY'
        };
        const patternLabelMap = {
            MONTHLY_FIXED: 'Monthly Fixed',
            VARIABLE: 'Variable',
            SEASONAL: 'Seasonal',
            DAILY: 'Daily',
            UNKNOWN: 'Not Set'
        };
        const patternKey = pc.cashFlowPattern || fallbackPatternByCategory[pc.category] || 'UNKNOWN';
        const patternLabel = patternLabelMap[patternKey] || patternKey;
        const severity = {
            'SALARIED': 'success',
            'BUSINESS': 'warning',
            'FARMER': 'info',
            'PROFESSIONAL': 'primary',
            'DAILY_WAGE': 'danger',
            'RETIRED': 'secondary',
            'OTHER': 'secondary'
        }[pc.category] || 'secondary';
        
        return (
            <div>
                <Tag value={pc.category} severity={severity} className="text-xs" />
                <div className="text-xs text-muted mt-1">{patternLabel}</div>
            </div>
        );
    }

    // Timing Context Body Template
    timingContextBody = (rowData) => {
        const pc = rowData.professionContext;
        
        if (!pc || !pc.timingReason) {
            return <span className="text-xs text-muted">N/A</span>;
        }
        
        // Check if timing override was applied
        if (rowData.timingOverride && rowData.timingOverride.applied) {
            return (
                <div className="timing-override">
                    <i className="pi pi-exclamation-triangle text-warning mr-1" />
                    <span className="text-xs font-semibold">{rowData.timingOverride.reason}</span>
                </div>
            );
        }
        
        return (
            <div className="timing-normal">
                <i className="pi pi-clock text-primary mr-1" />
                <span className="timing-reason-text">{pc.timingReason}</span>
            </div>
        );
    }

    actionsBody = (rowData) => {
        const { isApprovalMode } = this.props;
        
        const tooltipOptions = { position: 'top', mouseTrack: false, mouseTrackTop: 15 };
        const anyOpenActionId = this.getUserOpenActionIdForStudent(rowData.studentId);
        const hasAnyOpenAction = Boolean(anyOpenActionId);
        const hasUserOpenAction = this.hasUserOpenActionForStudent(rowData.studentId);
        
        const disableLogOutcome = hasAnyOpenAction && rowData.status === 'PENDING' && rowData._id !== anyOpenActionId;
        const disableFollowUp = hasAnyOpenAction || (rowData.status === 'COMPLETED' && this.state.followUpDebounce[rowData._id]);

        if (isApprovalMode) {
             // Approval Mode Actions
             return (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '40px', gap: '8px' }}>
                    <Authorizer permId={PERMISSIONS.FDE_APPROVE_ACTION}>
                        <Button 
                            icon="pi pi-check" 
                            className="p-button-rounded p-button-success" 
                            style={{ width: '32px', height: '32px', minWidth: '32px' }}
                            tooltip="Approve" 
                            tooltipOptions={tooltipOptions} 
                            onClick={() => this.approveAction(rowData)} 
                        />
                    </Authorizer>
                    <Authorizer permId={PERMISSIONS.FDE_OVERRIDE_ACTION}>
                        <Button 
                            icon="pi pi-times" 
                            className="p-button-rounded p-button-danger" 
                            style={{ width: '32px', height: '32px', minWidth: '32px' }}
                            tooltip="Reject/Override" 
                            tooltipOptions={tooltipOptions} 
                            onClick={() => this.openOverride(rowData)} 
                        />
                    </Authorizer>
                    <Authorizer permId={PERMISSIONS.FDE_VIEW_ACTION_HISTORY}>
                        <Button 
                            icon="pi pi-history" 
                            className="p-button-rounded p-button-secondary" 
                            style={{ width: '32px', height: '32px', minWidth: '32px' }}
                            tooltip="View History" 
                            tooltipOptions={tooltipOptions} 
                            onClick={() => this.viewHistory(rowData)} 
                        />
                    </Authorizer>
                </div>
            );
        }

        // Standard Queue Actions
        return (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: '40px', gap: '8px' }}>
                <Authorizer permId={PERMISSIONS.FDE_VIEW_ACTION_HISTORY}>
                    <Button 
                        icon="pi pi-history" 
                        className="p-button-rounded p-button-secondary" 
                        style={{ width: '32px', height: '32px', minWidth: '32px' }}
                        tooltip="View History" 
                        tooltipOptions={tooltipOptions} 
                        onClick={() => this.viewHistory(rowData)} 
                    />
                </Authorizer>
                {rowData.status === 'PENDING' && (
                    <>
                        <Authorizer permId={PERMISSIONS.FDE_OVERRIDE_ACTION}>
                            <Button 
                                icon="pi pi-pencil" 
                                className="p-button-rounded p-button-warning" 
                                style={{ width: '32px', height: '32px', minWidth: '32px' }}
                                tooltip="Override/Modify" 
                                tooltipOptions={tooltipOptions} 
                                onClick={() => this.openOverride(rowData)} 
                            />
                        </Authorizer>
                        <Authorizer permId={PERMISSIONS.FDE_LOG_CALL_OUTCOME}>
                            <Button 
                                icon="pi pi-check-square" 
                                className="p-button-rounded p-button-info" 
                                style={{ width: '32px', height: '32px', minWidth: '32px' }}
                                disabled={disableLogOutcome}
                                tooltip="Log Outcome" 
                                tooltipOptions={tooltipOptions} 
                                onClick={() => this.openOutcome(rowData)} 
                            />
                        </Authorizer>
                    </>
                )}
                {rowData.status === 'COMPLETED' && (
                    <>
                        <Authorizer permId={PERMISSIONS.FDE_LOG_CALL_OUTCOME}>
                            <Button 
                                icon="pi pi-pencil" 
                                className="p-button-rounded p-button-info" 
                                style={{ width: '32px', height: '32px', minWidth: '32px' }}
                                tooltip="Edit Outcome" 
                                tooltipOptions={tooltipOptions} 
                                onClick={() => this.openOutcome(rowData)} 
                            />
                        </Authorizer>
                        {(rowData.needsFollowUp || rowData.riskBand === 'CRITICAL' || rowData.riskBand === 'HIGH' || this.isPromiseBreached(rowData)) && (
                            <Authorizer permId={PERMISSIONS.FDE_CREATE_FOLLOWUP_ACTION}>
                                <Button 
                                    icon="pi pi-plus-circle" 
                                    className="p-button-rounded p-button-success"
                                    style={{ width: '32px', height: '32px', minWidth: '32px' }}
                                    disabled={disableFollowUp}
                                    tooltip={hasAnyOpenAction ? "Open action already exists for this student today" : "Create Follow-up Action"} 
                                    tooltipOptions={tooltipOptions} 
                                    onClick={() => this.confirmFollowUpAction(rowData)} 
                                />
                            </Authorizer>
                        )}
                    </>
                )}
                {rowData.isOverridden && (
                    <Authorizer permId={PERMISSIONS.FDE_OVERRIDE_ACTION}>
                        <Button 
                            icon="pi pi-file-edit" 
                            className="p-button-rounded p-button-warning" 
                            style={{ width: '32px', height: '32px', minWidth: '32px' }}
                            tooltip="Edit Override" 
                            tooltipOptions={tooltipOptions} 
                            onClick={() => this.openOverride(rowData)} 
                        />
                    </Authorizer>
                )}
            </div>
        );
    }

    confirmFollowUpAction = (action) => {
        const { followUpDebounce } = this.state;
        if (followUpDebounce[action._id]) {
            this.showToast('warn', 'In Progress', 'Follow-up action creation already in progress');
            return;
        }
        
        confirmDialog({
            message: `Create a new ${action.actionType} action for ${action.studentName}? This will be assigned to ${action.assignedTo}.`,
            header: 'Create Follow-up Action',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-success',
            accept: () => this.createFollowUpAction(action)
        });
    }

    createFollowUpAction = async (action) => {
        const { followUpDebounce } = this.state;
        
        // Set debounce flag to prevent duplicate requests
        const debounceMap = { ...followUpDebounce, [action._id]: true };
        this.setState({ followUpDebounce: debounceMap });
        
        this.setState({ loading: true });
        try {
            const academicYear = localStorage.getItem('userAcademicYear') || '2024-2025';
            const url = `${baseUrlAdmin}/fee-decision/actions`;
            const followUpData = {
                enrollmentNo: action.enrollmentNo,
                academicYear: academicYear,
                actionType: action.actionType, // Use updated action type based on current risk
                assignedTo: action.assignedTo, // Use updated assignee
                scheduledDate: new Date(),
                remarks: `Follow-up: ${action.riskBand} risk (${action.riskScore}). Previous action completed on ${new Date(action.outcome?.completedAt || Date.now()).toLocaleDateString()}`
            };

            const res = await this.service.post(url, followUpData, true);

            if (res?.status) {
                this.showToast('success', 'Success', `New ${action.actionType} action created for ${action.assignedTo}`);
                this.fetchActions();
            } else {
                const errorMsg = res?.res?.message || res?.message || 'Failed to create follow-up action';
                this.showToast('error', 'Error', errorMsg);
            }
        } catch (error) {
            console.error('Follow-up creation error:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Failed to create follow-up action';
            this.showToast('error', 'Error', errorMsg);
        } finally {
            // Clear debounce flag after request completes
            const updatedDebounce = { ...this.state.followUpDebounce };
            delete updatedDebounce[action._id];
            this.setState({ followUpDebounce: updatedDebounce });
            
            this.setState({ loading: false });
        }
    }

    openManualAction = () => {
        this.setState({
            manualDialog: true,
            manualData: {
                studentId: '',
                enrollmentNo: '', // User will enter this to identify student manually for now
                actionType: 'CALL',
                assignedTo: 'ACCOUNTS',
                scheduledDate: new Date(),
                remarks: ''
            }
        });
    }

    submitManualAction = async () => {
        const { manualData } = this.state;
        if (!manualData.enrollmentNo) {
            this.showToast('warn', 'Validation', 'Enrollment Number is required');
            return;
        }

        this.setState({ loading: true });
        try {
            const url = `${baseUrlAdmin}/fee-decision/actions`;
            const payload = {
                enrollmentNo: manualData.enrollmentNo,
                actionType: manualData.actionType,
                assignedTo: manualData.assignedTo,
                scheduledDate: manualData.scheduledDate,
                remarks: manualData.remarks,
                academicYear: localStorage.getItem('userAcademicYear')
            };

            const res = await this.service.post(url, payload, true);
            if (res?.status) {
                this.showToast('success', 'Success', 'Manual action created');
                this.setState({ manualDialog: false });
                this.fetchActions();
            }
        } catch (error) {
            this.showToast('error', 'Error', error.message || 'Failed to create action');
            this.setState({ loading: false });
        }
    }

    approveBulk = () => {
        const { selectedActions } = this.state;
        if (!selectedActions || selectedActions.length === 0) return;

        confirmDialog({
            message: `Approve ${selectedActions.length} selected actions?`,
            header: 'Bulk Approval',
            icon: 'pi pi-check-circle',
            accept: async () => {
                this.setState({ loading: true });
                try {
                    const url = `${baseUrlAdmin}/fee-decision/actions/bulk-approve`;
                    const payload = {
                        actionIds: selectedActions.map(a => a._id)
                    };
                    const res = await this.service.post(url, payload, true);
                    if (res?.status) {
                        this.showToast('success', 'Success', 'Bulk approval completed');
                        this.setState({ selectedActions: [] });
                        this.fetchActions();
                    }
                } catch (error) {
                    this.showToast('error', 'Error', error.message);
                    this.setState({ loading: false });
                }
            }
        });
    }

    render() {
        const { loading, actions, lazyParams, totalRecords, filters } = this.state;
        const { isApprovalMode } = this.props;

        const actionTypes = [
            { label: 'WhatsApp', value: 'WHATSAPP' },
            { label: 'Phone Call', value: 'CALL' },
            { label: 'Escalation', value: 'ESCALATION' }
        ];

        const roles = [
            { label: 'System', value: 'SYSTEM' },
            { label: 'Accounts', value: 'ACCOUNTS' },
            { label: 'Principal', value: 'PRINCIPAL' }
        ];

        return (
            <div className="action-queue-wrapper">
                {loading && <LoadingComponent />}
                <Toast ref={this.toast} position="bottom-right" />
                <ConfirmDialog />

                <div className="flex justify-content-between align-items-center mb-3">
                    <h2 className="page-title m-0">{isApprovalMode ? 'Action Approvals' : 'Prescriptive Action Queue (NBA)'}</h2>
                     <div className="flex gap-2">
                        {isApprovalMode && this.state.selectedActions.length > 0 && (
                            <Authorizer permId={PERMISSIONS.FDE_BULK_APPROVE_ACTIONS}>
                                <Button label={`Approve Selected (${this.state.selectedActions.length})`} icon="pi pi-check" className="p-button-success" onClick={this.approveBulk} />
                            </Authorizer>
                        )}
                        {!isApprovalMode && (
                            <>
                               {/*  <Authorizer permId={PERMISSIONS.FDE_CREATE_MANUAL_ACTION}>
                                    <Button label="Manual Action" icon="pi pi-plus" onClick={this.openManualAction} className="p-button-secondary" />
                                </Authorizer> */}
                                <Authorizer permId={PERMISSIONS.FDE_GENERATE_DAILY_ACTIONS}>
                                    <Button label="Generate Daily Actions" icon="pi pi-refresh" onClick={this.generateActions} className="p-button-outlined" />
                                </Authorizer>
                            </>
                        )}
                    </div>
                </div>

                <Card className="mb-3">
                    <div className="flex gap-3 align-items-end flex-wrap">
                        <div style={{ width: '250px' }}>
                            <label className="block mb-2 text-sm font-bold">Search</label>
                            <InputText 
                                value={this.state.tempFilters.search} 
                                onChange={(e) => this.setState({ tempFilters: { ...this.state.tempFilters, search: e.target.value }})} 
                                placeholder="Search Student..." 
                                className="w-full"
                            />
                        </div>
                        {!isApprovalMode && (
                            <div style={{ width: '200px' }}>
                                <label className="block mb-2 text-sm font-bold">Filter by Status</label>
                                <Dropdown 
                                    value={this.state.tempFilters.status} 
                                    options={['PENDING', 'NEEDS_APPROVAL', 'APPROVED', 'COMPLETED', 'OVERRIDDEN']} 
                                    onChange={(e) => this.setState({ tempFilters: { ...this.state.tempFilters, status: e.value }})}
                                    placeholder="All Statuses" 
                                    showClear 
                                    className="w-full"
                                />
                            </div>
                        )}
                        <div style={{ width: '200px' }}>
                            <label className="block mb-2 text-sm font-bold">Filter by Assignee</label>
                            <Dropdown 
                                value={this.state.tempFilters.assignedTo} 
                                options={['SYSTEM', 'ACCOUNTS', 'PRINCIPAL', 'COORDINATOR']} 
                                onChange={(e) => this.setState({ tempFilters: { ...this.state.tempFilters, assignedTo: e.value }})}
                                placeholder="All Roles" 
                                showClear 
                                className="w-full"
                            />
                        </div>

                        {/* Search and Clear Buttons moved to the end of the row */}
                        <div className="flex gap-2">
                            <Button 
                                label="Search" 
                                icon="pi pi-search" 
                                onClick={() => this.setState({ filters: { ...this.state.tempFilters }, lazyParams: { ...this.state.lazyParams, page: 1, first: 0 } }, this.fetchActions)} 
                            />
                            <Button 
                                label="Clear" 
                                icon="pi pi-times" 
                                className="p-button-secondary p-button-outlined" 
                                onClick={() => {
                                    const defaultStatus = isApprovalMode ? 'NEEDS_APPROVAL' : '';
                                    const resetFilters = { search: '', status: defaultStatus, assignedTo: '' };
                                    this.setState({ 
                                        tempFilters: resetFilters, 
                                        filters: resetFilters,
                                        lazyParams: { ...this.state.lazyParams, page: 1, first: 0 } 
                                    }, this.fetchActions);
                                }} 
                            />
                        </div>
                    </div>
                </Card>

                <div className="card-custom">
                    <DataTable 
                        value={actions} 
                        scrollable 
                        scrollHeight="600px"
                        lazy paginator first={lazyParams.first} rows={lazyParams.rows} 
                        totalRecords={totalRecords} onPage={this.onPage}
                        emptyMessage="No actions pending."
                        rowClassName={(data) => (data.needsAttention ? 'action-row-urgent' : '')}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        selection={this.state.selectedActions} 
                        onSelectionChange={(e) => this.setState({ selectedActions: e.value })}
                        dataKey="_id"
                        stripedRows
                    >
                        {isApprovalMode && <Column selectionMode="multiple" style={{ width: '3rem' }} />}
                        <Column field="studentName" header="Student Name" sortable style={{ width: '150px', textAlign: 'left' }} headerStyle={{ textAlign: 'left' }} />
                        <Column field="enrollmentNo" header="Enrollment No" sortable style={{ width: '120px', textAlign: 'left' }} headerStyle={{ textAlign: 'left' }} />
                        <Column field="className" header="Class" sortable style={{ width: '80px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                        <Column field="riskBand" header="Risk" body={this.riskBody} style={{ width: '100px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                        <Column header="Profession" body={this.professionBody} style={{ width: '120px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                        <Column header="Timing Context" body={this.timingContextBody} style={{ width: '180px', textAlign: 'left' }} headerStyle={{ textAlign: 'left' }} />
                        <Column field="triggerReason" header="Trigger" style={{ width: '140px', textAlign: 'left' }} headerStyle={{ textAlign: 'left' }} />
                        <Column field="scriptTone" header="Tone" body={(r) => r.scriptTone ? <span className="text-xs font-semibold px-2 py-1 surface-200 border-round">{r.scriptTone}</span> : '-'} style={{ width: '80px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                        <Column field="actionType" header="Action" body={(r) => <Tag value={r.actionType} severity={r.actionType === 'ESCALATION' ? 'danger' : 'info'} />} style={{ width: '100px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                        <Column field="assignedTo" header="Assigned To" style={{ width: '120px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                        <Column field="scheduledDate" header="Schedule" body={this.dateBody} style={{ width: '110px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                        <Column field="lastOutcome" header="Last Outcome" body={this.lastOutcomeBody} style={{ width: '160px', textAlign: 'left' }} headerStyle={{ textAlign: 'left' }} /> 
                        {!isApprovalMode && <Column field="status" header="Status" body={this.statusBody} style={{ width: '100px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />}
                        <Column body={this.actionsBody} header="Actions" style={{ width: '150px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                    </DataTable>
                </div>

                {/* Override Dialog */}
                <Dialog header="Override Action" visible={this.state.overrideDialog} style={{ width: '450px' }} 
                    footer={
                        <div>
                            <Button label="Cancel" icon="pi pi-times" onClick={() => this.setState({ overrideDialog: false })} className="p-button-text" />
                            <Button label="Confirm Override" icon="pi pi-check" onClick={this.submitOverride} autoFocus />
                        </div>
                    } 
                    onHide={() => this.setState({ overrideDialog: false })}>
                    
                    <div className="field">
                        <label>Action Type</label>
                        <Dropdown value={this.state.overrideData.actionType} options={actionTypes} 
                            onChange={(e) => this.setState({ overrideData: { ...this.state.overrideData, actionType: e.value }})} className="w-full" />
                    </div>
                    <div className="field">
                        <label>Assign To</label>
                        <Dropdown value={this.state.overrideData.assignedTo} options={roles} 
                            onChange={(e) => this.setState({ overrideData: { ...this.state.overrideData, assignedTo: e.value }})} className="w-full" />
                    </div>
                    <div className="field">
                        <label>Schedule Date</label>
                        <Calendar value={this.state.overrideData.scheduledDate} 
                            onChange={(e) => this.setState({ overrideData: { ...this.state.overrideData, scheduledDate: e.value }})} className="w-full" showIcon minDate={new Date()} />
                    </div>
                    <div className="field">
                        <label>Justification (Required)</label>
                        <InputTextarea rows={3} value={this.state.overrideData.justification} 
                             onChange={(e) => this.setState({ overrideData: { ...this.state.overrideData, justification: e.target.value }})} className="w-full" />
                    </div>
                </Dialog>

                {/* Outcome Dialog */}
                <Dialog header="Log Outcome" visible={this.state.outcomeDialog} style={{ width: '450px' }}
                     footer={
                        <div>
                            <Button label="Cancel" icon="pi pi-times" onClick={() => this.setState({ outcomeDialog: false })} className="p-button-text" />
                            <Button label="Save Outcome" icon="pi pi-check" onClick={this.submitOutcome} autoFocus />
                        </div>
                    }
                    onHide={() => this.setState({ outcomeDialog: false })}>
                    
                    <div className="field">
                        <label>Outcome Status</label>
                        <Dropdown value={this.state.outcomeData.status} options={this.outcomeOptions} 
                            onChange={(e) => this.setState({ outcomeData: { ...this.state.outcomeData, status: e.value }})} className="w-full" />
                    </div>
                    {this.state.outcomeData.status === 'PROMISED' && (
                        <div className="field">
                            <label>Promised Date</label>
                            <Calendar value={this.state.outcomeData.promiseDate} 
                                onChange={(e) => this.setState({ outcomeData: { ...this.state.outcomeData, promiseDate: e.value }})} 
                                className="w-full" showIcon minDate={new Date()} dateFormat="dd/mm/yy" />
                        </div>
                    )}
                    <div className="field">
                        <label>Remarks</label>
                        <InputTextarea rows={3} value={this.state.outcomeData.remarks} 
                             onChange={(e) => this.setState({ outcomeData: { ...this.state.outcomeData, remarks: e.target.value }})} className="w-full" />
                    </div>
                </Dialog>

                {/* History Dialog */}
                <Dialog header={`History for ${this.state.selectedStudent?.name}`} visible={this.state.historyDialog} style={{ width: '600px' }}
                    onHide={() => this.setState({ historyDialog: false })}>
                    <div className="history-timeline">
                        {this.state.studentHistory.length === 0 ? (
                            <p className="text-center">No previous actions found.</p>
                        ) : (
                            this.state.studentHistory.map((h, i) => {
                                const hDate = new Date(h.scheduledDate);
                                const formattedDate = `${hDate.getUTCDate().toString().padStart(2, '0')}/${(hDate.getUTCMonth() + 1).toString().padStart(2, '0')}/${hDate.getUTCFullYear()}`;
                                return (
                                <div key={i} className="mb-3 border-bottom-1 surface-border pb-2">
                                    <div className="flex justify-content-between">
                                        <span className="font-bold">{formattedDate}</span>
                                        <Tag value={h.status} severity={h.status === 'COMPLETED' ? 'success' : 'warning'} />
                                    </div>
                                    <div className="mt-1">
                                        <span className="font-semibold">{h.actionType}</span> - {h.outcome?.status || 'No Context'}
                                    </div>
                                    {h.outcome?.remarks && <div className="text-sm text-gray-500 mt-1">"{h.outcome.remarks}"</div>}
                                </div>
                                );
                            })
                        )}
                    </div>
                </Dialog>

                {/* Manual Action Dialog */}
                <Dialog header="Create Manual Action" visible={this.state.manualDialog} style={{ width: '450px' }}
                    footer={
                        <div>
                            <Button label="Cancel" icon="pi pi-times" onClick={() => this.setState({ manualDialog: false })} className="p-button-text" />
                            <Button label="Create" icon="pi pi-check" onClick={this.submitManualAction} autoFocus />
                        </div>
                    }
                    onHide={() => this.setState({ manualDialog: false })}>
                    
                    <div className="field">
                        <label className="block mb-2 font-bold">Search Student (Enrollment No)</label>
                        <InputText value={this.state.manualData.enrollmentNo} 
                             onChange={(e) => this.setState({ manualData: { ...this.state.manualData, enrollmentNo: e.target.value }})} 
                             placeholder="Enter Enrollment Number" className="w-full" />
                         <small className="block mt-1 text-gray-500">Enter exact enrollment number to link student.</small>
                    </div>

                    <div className="field mt-3">
                        <label className="block mb-2 font-bold">Action Type</label>
                        <Dropdown value={this.state.manualData.actionType} options={actionTypes} 
                            onChange={(e) => this.setState({ manualData: { ...this.state.manualData, actionType: e.value }})} className="w-full" />
                    </div>
                     <div className="field mt-3">
                        <label className="block mb-2 font-bold">Assign To</label>
                        <Dropdown value={this.state.manualData.assignedTo} options={roles} 
                            onChange={(e) => this.setState({ manualData: { ...this.state.manualData, assignedTo: e.value }})} className="w-full" />
                    </div>
                    <div className="field mt-3">
                        <label className="block mb-2 font-bold">Schedule Date</label>
                        <Calendar value={this.state.manualData.scheduledDate} 
                            onChange={(e) => this.setState({ manualData: { ...this.state.manualData, scheduledDate: e.value }})} className="w-full" showIcon minDate={new Date()} />
                    </div>
                     <div className="field mt-3">
                        <label className="block mb-2 font-bold">Remarks</label>
                        <InputTextarea rows={2} value={this.state.manualData.remarks} 
                             onChange={(e) => this.setState({ manualData: { ...this.state.manualData, remarks: e.target.value }})} className="w-full" />
                    </div>
                </Dialog>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.currentUserPerms ? state.currentUserPerms.userInfo : {}
});

export default connect(mapStateToProps)(withRouter(ActionQueue));
